import { ApiResponse, OpsType } from '../../types';
import { apiClient } from '../client';

export interface RomaneioCadastroView {
  dbName: string;
  ops: OpsType;
}

export interface RomaneioCheckoutSummary {
  dbName: string;
  ops?: OpsType;
}

export interface RomaneioCheckoutSubmit {
  dbName: string;
  ops: OpsType;
  checkoutDate: string;
  userInfo: { email: string };
}

export interface RomaneioStatusPerson {
  numeroDocumento: string;
  dbName: string;
  ops?: OpsType;
}

export interface RomaneioTEDDownload {
  filterDate: string;
  type: string;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioPaymentSummary {
  filterDate: string;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioStatistics {
  filterDate: string;
  type: string;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioAntecipacaoViz {
  filterDate: string;
  dbName: string;
  ops: OpsType;
}

export interface EntregoCadastroSearch {
  searchType: 'cpf' | 'cnpj' | 'phone';
  searchValue: string;
  dbName: string;
  ops: OpsType;
}

export interface EntregoCadastroUpdate {
  documento: string;
  active: boolean;
  dbName: string;
  ops: OpsType;
}

export interface EntregoCadastroBulkUpdate {
  documentos: string[];
  active: boolean;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioCadastroDocViewParams {
  telefone?: string;
  cpf?: string;
  cnpj?: string;
  dbName: string;
}

export interface RomaneioCadastroDocViewResult {
  id: number;
  nao_aprovado_azumi: boolean;
  nome_completo: string;
  data_nascimento: string | null;
  cpf: string | null;
  cnpj: string | null;
  telefone: string;
  ops: string;
  ifood_uuid: string | null;
  ativo: boolean;
}

export interface RomaneioCadastroDocViewResponse {
  cadastro: RomaneioCadastroDocViewResult[];
  allOps: { ops: string }[];
}

export interface RomaneioCadastroUpdateParams {
  documento: string;
  ativo: boolean;
  dbName: string;
  ops: string;
}

export interface RomaneioCadastroUpdateResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    nome_completo: string;
    cpf: string;
    telefone: string;
    ops: string;
    ativo: boolean;
  };
}

export interface BlockedUserFilters {
  block_ativo?: boolean;
  documento?: string;
  created_by?: string;
}

export interface BlockedUsersListParams {
  dbName: string;
  ops: string;
  page?: number;
  limit?: number;
  filters?: BlockedUserFilters;
}

export interface BlockedUser {
  id: number;
  numero_documento: string;
  cpf: string | null;
  cnpj: string | null;
  ops: string;
  motivo_bloqueio: string;
  block_ativo: boolean;
  created_at: string;
  created_by: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface BlockedUsersListResponse {
  success: boolean;
  items: BlockedUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface EntregoFechamentoParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentWeek?: boolean;
}

export interface MuveFechamentoParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentWeek?: boolean;
}

export interface EntregoSaldoParams {
  /** Busca por telefone (apenas dígitos) */
  telefone?: string;
  /** Busca por CPF (11 dígitos) */
  cpf?: string;
  /** Busca por CNPJ (14 dígitos) */
  cnpj?: string;
  dbName: string;
}

export interface EntregoNFSListParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentWeek?: boolean;
}

export interface EntregoNFSZipParams {
  ops: string;
  segunda?: string;
  allowCurrentWeek?: boolean;
}

export interface EntregoNFSZipStatusParams {
  ops: string;
  /** Chave S3 devolvida por gestorEntregoNFSZip, ex.: "nfs-zip/OPS/2025-01-06_2025-01-12.zip" */
  jobKey: string;
}

export interface EntregoSRListParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). */
  allowCurrentWeek?: boolean;
}

export const romaneioService = {
  // Get saldo remanescente for an entregador by CPF/CNPJ
  async getSaldoEntrego(params: EntregoSaldoParams): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoSaldo', params);
  },

  // Get entrego weekly closing data (resumo + operacoes)
  async getEntregoFechamento(
    params: EntregoFechamentoParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoFechamento', params);
  },

  // Get muve weekly closing data (resumo + operacoes)
  async getMuveFechamento(
    params: MuveFechamentoParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorMuveFechamento', params);
  },

  // Get InDrive closing data grouped by vencimento (sexta-feira)
  async getIndriveFechamento(
    params: { ops: string; sexta?: string; allowOpenCycle?: boolean }
  ): Promise<ApiResponse> {
    return apiClient.post('/indriveFechamento', params);
  },

  // Get NFS list for the week (antecipacoes + saldos remanescentes)
  async getEntregoNFSList(
    params: EntregoNFSListParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoNFSList', params);
  },

  // Request async ZIP generation — returns { jobKey } immediately
  async getEntregoNFSZip(
    params: EntregoNFSZipParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoNFSZip', params);
  },

  // Check if the async ZIP job is ready; returns presigned URL if so
  async getEntregoNFSZipStatus(
    params: EntregoNFSZipStatusParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoNFSZipStatus', params);
  },

  // Get SR (Saldo Remanescente) list for the week
  async getEntregoSRList(
    params: EntregoSRListParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoSRList', params);
  },

  // View delivery personnel registration data
  async getCadastroView(params: RomaneioCadastroView): Promise<ApiResponse> {
    return apiClient.post('/romaneioCadastroView', params);
  },

  // Get checkout summary for deliveries
  async getCheckoutSummary(
    params: RomaneioCheckoutSummary
  ): Promise<ApiResponse> {
    return apiClient.post('/romaneioCheckoutSummary', params);
  },

  // Submit checkout (freeze records and insert into romaneio_checkout)
  async submitCheckout(params: RomaneioCheckoutSubmit): Promise<ApiResponse> {
    return apiClient.post('/romaneioCheckoutSubmmit', params);
  },

  // Get delivery status for a specific person
  async getStatusPerson(params: RomaneioStatusPerson): Promise<ApiResponse> {
    return apiClient.post('/romaneioStatusPerson', params);
  },

  // Download TED transfer data
  async downloadTED(params: RomaneioTEDDownload): Promise<ApiResponse> {
    return apiClient.post('/romaneioTEDDownload', params);
  },

  // Get payment summary for deliveries
  async getPaymentSummary(
    params: RomaneioPaymentSummary
  ): Promise<ApiResponse> {
    return apiClient.post('/romaneioPaymentSummary', params);
  },

  // Get delivery statistics
  async getStatistics(params: RomaneioStatistics): Promise<ApiResponse> {
    return apiClient.post('/romaneioStatistics', params);
  },

  // Get advance payment visualization
  async getAntecipacaoViz(
    params: RomaneioAntecipacaoViz
  ): Promise<ApiResponse> {
    return apiClient.post('/romaneioAntecipacaoViz', params);
  },

  // Search entrego registration by document or phone
  async searchEntregoCadastro(
    params: EntregoCadastroSearch
  ): Promise<ApiResponse> {
    return apiClient.post('/entregoCadastroSearch', params);
  },

  // Update entrego registration status
  async updateEntregoCadastro(
    params: EntregoCadastroUpdate
  ): Promise<ApiResponse> {
    return apiClient.post('/entregoCadastroUpdate', params);
  },

  // Bulk update entrego 1egistration status
  async bulkUpdateEntregoCadastro(
    params: EntregoCadastroBulkUpdate
  ): Promise<ApiResponse> {
    return apiClient.post('/entregoCadastroBulkUpdate', params);
  },

  async searchCadastroDoc(
    params: RomaneioCadastroDocViewParams
  ): Promise<ApiResponse<{ data: RomaneioCadastroDocViewResponse }>> {
    return apiClient.post('/romaneioCadastroDocView', params);
  },

  async updateCadastroStatus(
    params: RomaneioCadastroUpdateParams
  ): Promise<ApiResponse<RomaneioCadastroUpdateResponse>> {
    return apiClient.post('/romaneioCadastroUpdate', params);
  },

  async getBlockedUsers(
    params: BlockedUsersListParams
  ): Promise<ApiResponse<BlockedUsersListResponse>> {
    return apiClient.post('/romaneioCadastroBlockList', params);
  },
};
